// ui/floatingNotifications.js
// Floating XP and item gain notifications with batching

import { skillRegistry, getSkillIcon } from '../core/skillRegistry.js';
import { getItemIcon, getItemColor } from '../data/items/index.js';
import { IS_DEV } from '../config/env.js';
import { adminPanel } from '../skills/admin/admin.js';

// Check if we're in a browser environment
const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

// Container for floating notifications - positioned near inventory panel
let notificationContainer = null;

// Batching system - aggregate notifications that come in quickly
const BATCH_DELAY_MS = 150; // Time window to batch notifications
const pendingXP = new Map(); // skillId -> { amount, timeoutId }
const pendingItems = new Map(); // itemId -> { quantity, timeoutId }
let pendingGold = { amount: 0, timeoutId: null };

/**
 * Initialize the notification container
 */
function initContainer() {
    if (!isBrowser || notificationContainer) return;

    notificationContainer = document.createElement('div');
    notificationContainer.className = 'floating-notifications';
    document.body.appendChild(notificationContainer);
}

/**
 * Create and show a notification element
 */
function createNotification(className, innerHTML) {
    const notification = document.createElement('div');
    notification.className = `floating-notification ${className}`;
    notification.innerHTML = innerHTML;

    notificationContainer.appendChild(notification);

    // Trigger animation
    requestAnimationFrame(() => {
        notification.classList.add('float-up');
    });

    // Remove after animation
    setTimeout(() => {
        if (notification.parentNode) {
            notification.parentNode.removeChild(notification);
        }
    }, 1500);
}

/**
 * Show a floating XP gain notification (batched)
 * @param {string} skillId - The skill ID
 * @param {number} xpAmount - Amount of XP gained
 */
export function showXPGain(skillId, xpAmount) {
    if (!isBrowser) return;
    initContainer();

    const pending = pendingXP.get(skillId);

    if (pending) {
        // Add to existing pending batch
        pending.amount += xpAmount;
        // Reset the timer
        clearTimeout(pending.timeoutId);
        pending.timeoutId = setTimeout(() => flushXP(skillId), BATCH_DELAY_MS);
    } else {
        // Start new batch
        pendingXP.set(skillId, {
            amount: xpAmount,
            timeoutId: setTimeout(() => flushXP(skillId), BATCH_DELAY_MS)
        });
    }
}

/**
 * Flush pending XP notification for a skill
 */
function flushXP(skillId) {
    const pending = pendingXP.get(skillId);
    if (!pending) return;

    pendingXP.delete(skillId);

    const icon = getSkillIcon(skillId);

    createNotification('xp-notification', `
        <span class="notification-icon">${icon}</span>
        <span class="notification-value">+${formatXP(pending.amount)} XP</span>
    `);
}

/**
 * Check if we should show notifications (skip in high-speed mode)
 */
function shouldShowNotifications() {
    if (!isBrowser) return false;
    const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
    return speedMult <= 2;
}

/**
 * Show a floating item gain notification (batched)
 * @param {string} itemId - The item ID
 * @param {number} quantity - Amount gained
 * @param {boolean} checkSpeed - Whether to check speed mode (default true)
 */
export function showItemGain(itemId, quantity, checkSpeed = true) {
    if (!isBrowser) return;
    if (checkSpeed && !shouldShowNotifications()) return;
    initContainer();

    const pending = pendingItems.get(itemId);

    if (pending) {
        // Add to existing pending batch
        pending.quantity += quantity;
        // Reset the timer
        clearTimeout(pending.timeoutId);
        pending.timeoutId = setTimeout(() => flushItem(itemId), BATCH_DELAY_MS);
    } else {
        // Start new batch
        pendingItems.set(itemId, {
            quantity: quantity,
            timeoutId: setTimeout(() => flushItem(itemId), BATCH_DELAY_MS)
        });
    }
}

/**
 * Flush pending item notification
 */
function flushItem(itemId) {
    const pending = pendingItems.get(itemId);
    if (!pending) return;

    pendingItems.delete(itemId);

    const icon = getItemIcon(itemId, pending.quantity) || '?';
    const color = getItemColor(itemId) || '#888';

    createNotification('item-notification', `
        <span class="notification-icon item-icon" style="background-color: ${color}">${icon}</span>
        <span class="notification-value">+${pending.quantity}</span>
    `);
}

/**
 * Show a floating gold gain notification (batched)
 * @param {number} amount - Amount of gold gained
 */
export function showGoldGain(amount) {
    if (!isBrowser) return;
    initContainer();

    pendingGold.amount += amount;

    if (pendingGold.timeoutId) {
        clearTimeout(pendingGold.timeoutId);
    }

    pendingGold.timeoutId = setTimeout(flushGold, BATCH_DELAY_MS);
}

/**
 * Flush pending gold notification
 */
function flushGold() {
    if (pendingGold.amount === 0) return;

    const amount = pendingGold.amount;
    pendingGold.amount = 0;
    pendingGold.timeoutId = null;

    createNotification('gold-notification', `
        <span class="notification-icon">${getItemIcon('coins')}</span>
        <span class="notification-value">+${formatNumber(amount)}</span>
    `);
}

/**
 * Format XP for display
 */
function formatXP(xp) {
    if (xp >= 1000000) return (xp / 1000000).toFixed(1) + 'M';
    if (xp >= 1000) return (xp / 1000).toFixed(1) + 'K';
    return Math.floor(xp).toString();
}

/**
 * Format number for display
 */
function formatNumber(num) {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
}

// Expose to window for easy access
if (isBrowser) {
    window.floatingNotifications = {
        showXPGain,
        showItemGain,
        showGoldGain,
    };
}

export default {
    showXPGain,
    showItemGain,
    showGoldGain,
};
